import React, { useState, useEffect, useRef } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import {
  ArchiveBoxIcon,
  DocumentIcon,
  CircleStackIcon,
  ChartBarIcon
} from "@heroicons/react/24/outline"
import DependencyResolver from "./dependency-resolver"

export default function DependenciesDialog({ isOpen, onClose, controller }) {
  const computeTask = useRef(null)
  const { computeDependencies } = DependencyResolver()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isOpen) return
    setIsLoading(true)
    ;(async () => {
      computeTask.current = await computeDependencies(controller.getItems())
      setIsLoading(false)
    })()
  }, [isOpen])

  const DependencyViz = () => {
    if (!computeTask.current) return null

    // Transform the main data and sort by value
    const mainData = Object.entries(computeTask.current)
      .filter(([key]) => key !== "files")
      .map(([name, value]) => ({ name, value }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value)

    // Transform the files data
    const filesData = Object.entries(computeTask.current.files)
      .filter(([key, value]) => key !== "total" && value > 0)
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }))
      .sort((a, b) => b.value - a.value)

    // Calculate totals
    const totalRecords = mainData.reduce((acc, item) => acc + item.value, 0)
    const totalFiles = computeTask.current.files?.total || 0

    // Show no data message if there's no data to display
    if (mainData.length === 0 && filesData.length === 0) {
      return (
        <div className="flex items-center justify-center w-full h-[400px]">
          <Typography variant="admin_body">
            No data matches the current criteria
          </Typography>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {/* Summary Cards Row */}
        <div className="grid grid-cols-2 gap-3">
          {/* Total Records Card */}
          <div className="bg-admin_canvas border border-admin_bordered rounded p-3">
            <div className="flex items-center gap-2">
              <CircleStackIcon className="w-5 h-5 text-admin_primary" />
              <div>
                <Typography variant="admin_body_dim" className="text-xs mb-0">
                  Records
                </Typography>
                <Typography variant="admin_h3" className="text-xl font-bold">
                  {totalRecords.toLocaleString()}
                </Typography>
              </div>
            </div>
          </div>

          {/* Total Files Card */}
          <div className="bg-admin_canvas border border-admin_bordered rounded p-3">
            <div className="flex items-center gap-2">
              <DocumentIcon className="w-5 h-5 text-admin_success" />
              <div>
                <Typography variant="admin_body_dim" className="text-xs mb-0">
                  Files
                </Typography>
                <Typography variant="admin_h3" className="text-xl font-bold">
                  {totalFiles.toLocaleString()}
                </Typography>
              </div>
            </div>
          </div>
        </div>

        {/* Content Types Section */}
        {mainData.length > 0 && (
          <div className="bg-admin_canvas border border-admin_bordered rounded p-3">
            <div className="flex items-center gap-2 mb-2">
              <ArchiveBoxIcon className="w-4 h-4 text-admin_text" />
              <Typography variant="admin_body" className="font-semibold text-sm">
                Content Types
              </Typography>
            </div>
            <div className="space-y-1">
              {mainData.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 px-3 bg-admin_paper rounded hover:bg-admin_canvas/50 transition-colors"
                >
                  <Typography variant="admin_body" className="text-sm">
                    {item.name}
                  </Typography>
                  <Typography variant="admin_body" className="text-admin_primary font-semibold text-sm">
                    {item.value.toLocaleString()}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Types Section */}
        {filesData.length > 0 && (
          <div className="bg-admin_canvas border border-admin_bordered rounded p-3">
            <div className="flex items-center gap-2 mb-2">
              <DocumentIcon className="w-4 h-4 text-admin_text" />
              <Typography variant="admin_body" className="font-semibold text-sm">
                File Types
              </Typography>
            </div>
            <div className="space-y-1">
              {filesData.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-2 px-3 bg-admin_paper rounded hover:bg-admin_canvas/50 transition-colors"
                >
                  <Typography variant="admin_body" className="text-sm">
                    {item.name}
                  </Typography>
                  <Typography variant="admin_body" className="text-admin_success font-semibold text-sm">
                    {item.value.toLocaleString()}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal
      title="Dependencies"
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_compact"
    >
      {isLoading ? (
        <div className="flex items-center justify-center w-full h-[200px]">
          <Spinner
            variant="primary"
            size="md"
            thickness="medium"
            speed="normal"
            color="primary"
          />
        </div>
      ) : (
        <div className="px-3">
          <DependencyViz />
        </div>
      )}
    </Modal>
  )
}
